import { Box } from '@mui/system'
import { ProfessionalEntityDialog } from 'NewVersion/components/Professional/ProfessionalEntityDialogComponent'
import { ProfessionalTable } from 'NewVersion/components/Professional/ProfessionalTable'
import { Filter, VIEW_TYPE_GRID, VIEW_TYPE_KEY } from 'NewVersion/components/UI/Filters/Filter'
import FilterSearch from 'NewVersion/components/UI/Filters/FilterSearch'
import { Pagination } from 'NewVersion/components/UI/Pagination/Pagination'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import 'NewVersion/styles/ProfessionalsListPage.scss'
import { useEdition } from 'hooks/useEdition'
import { useFavourites } from 'hooks/useFavourites'
import { useGuillotina } from 'hooks/useGuillotina'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { PROCEDENCIES_PROFESSIONAL } from 'utils/constants'
import { ENS_TYPES_OBJ } from 'utils/entityUtils'
import { getCurrentJWToken, getCurrentLanguage } from 'utils/utils'

// Constants for chunking
const CHUNK_SIZE = 50
const PROFESSIONALS_ITEMS_PER_PAGE = 100

/**
 * Hook for server-side pagination (when NO favorites filter is active)
 */
const useProfessionalsWithServerPagination = ({
  edition,
  filtersSearch,
  paginationFilter,
  enabled,
}) => {
  const { isLoading, data: { items: professionals, items_total: showingItems } = [] } =
    useGuillotina({
      path: enabled
        ? `${edition}/@search?type_name__in=AcreditacioProfessional,AcreditacioPremsa${filtersSearch}${paginationFilter}`
        : null,
    })

  return {
    isLoading: enabled ? isLoading : false,
    professionals: enabled ? professionals || [] : [],
    showingItems: enabled ? showingItems || 0 : 0,
  }
}

/**
 * Hook for client-side pagination (when favorites filter is active)
 * Makes parallel chunk requests using Promise.all and paginates on the client side
 */
const useProfessionalsWithClientPagination = ({
  edition,
  filtersSearch,
  favouriteIds,
  currentPage,
  isShowingFavourites, // true = show favorites, false = show non-favorites
  enabled,
}) => {
  const [allProfessionals, setAllProfessionals] = useState([])
  const [isLoadingChunks, setIsLoadingChunks] = useState(false)
  const token = getCurrentJWToken()

  // Load data in parallel with chunks or in a single request
  useEffect(() => {
    if (!enabled) {
      setAllProfessionals([])
      setIsLoadingChunks(false)
      return
    }

    const loadData = async () => {
      setIsLoadingChunks(true)
      try {
        if (isShowingFavourites) {
          // For favorites = YES: split into chunks and make parallel requests
          if (!favouriteIds || favouriteIds.length === 0) {
            setAllProfessionals([])
            return
          }

          const chunks = []
          for (let i = 0; i < favouriteIds.length; i += CHUNK_SIZE) {
            chunks.push(favouriteIds.slice(i, i + CHUNK_SIZE))
          }

          const chunkPromises = chunks.map((chunk) => {
            const chunkFilter = `&uuid__or=${chunk.map((item) => `uuid=${item}`).join('%26')}`
            return fetchGuillotina({
              path: `${edition}/@search?type_name__in=AcreditacioProfessional,AcreditacioPremsa${filtersSearch}${chunkFilter}&b_size=1000`,
              token,
              checkError: true,
            })
          })

          const results = await Promise.all(chunkPromises)
          const combinedProfessionals = results.reduce((acc, result) => {
            if (result?.items) {
              return [...acc, ...result.items]
            }
            return acc
          }, [])

          setAllProfessionals(combinedProfessionals)
        } else {
          // For favorites = NO: single request and filter on the client
          const result = await fetchGuillotina({
            path: `${edition}/@search?type_name__in=AcreditacioProfessional,AcreditacioPremsa${filtersSearch}&b_size=10000`,
            token,
            checkError: true,
          })

          let professionals = result?.items || []

          // Exclude favorites
          if (favouriteIds && favouriteIds.length > 0) {
            professionals = professionals.filter((item) => !favouriteIds.includes(item.uuid))
          }

          setAllProfessionals(professionals)
        }
      } catch (error) {
        console.error('Error loading professionals:', error)
        setAllProfessionals([])
      } finally {
        setIsLoadingChunks(false)
      }
    }

    loadData()
  }, [enabled, edition, filtersSearch, isShowingFavourites, token, favouriteIds?.length])

  // Apply client-side pagination
  const startIndex = (currentPage - 1) * PROFESSIONALS_ITEMS_PER_PAGE
  const endIndex = startIndex + PROFESSIONALS_ITEMS_PER_PAGE
  const paginatedProfessionals = allProfessionals.slice(startIndex, endIndex)

  return {
    isLoading: isLoadingChunks,
    professionals: paginatedProfessionals,
    showingItems: allProfessionals.length,
  }
}

function ProfessionalsListPage() {
  const { t } = useTranslation()
  const { edition, currentEdition } = useEdition()
  const [searchParams, setSearchParams] = useSearchParams()
  const [professionalEntityStack, setProfessionalEntityStack] = useState([])
  const currentLanguage = getCurrentLanguage()
  const { favouiteIds } = useFavourites()

  // Detect if favorites filter is active
  const isFavouritesFilterActive =
    searchParams.get('favourites') && searchParams.get('favourites') !== 'all'

  const getFiltersSearch = () => {
    let filterPerfil = ''
    let filterInterestArtistic = ''
    let filterProcedencia = ''
    let order = ''
    let search = ''

    if (searchParams.get('perfil') && searchParams.get('perfil') !== 'all') {
      const perfilArray = searchParams.get('perfil').split(',')
      if (perfilArray[0] === 'artista' || perfilArray[0] === 'representant') {
        filterPerfil = `&ens_tipus__or=${perfilArray
          .map((item) => `ens_tipus=${item}`)
          .join('%26')}`
      } else {
        filterPerfil = `&ens_type_name__or=${perfilArray
          .map((item) => `ens_type_name=${ENS_TYPES_OBJ[item].ens_type_name}`)
          .join('%26')}`
      }
    }

    if (searchParams.get('interes_artistic') && searchParams.get('interes_artistic') !== 'all') {
      const interes_artistic = searchParams.get('interes_artistic').split(',')
      filterInterestArtistic = `&interes_artistic__in=${interes_artistic}`
    }

    if (searchParams.get('procedencia') && searchParams.get('procedencia') !== 'all') {
      const procedenciaArray = searchParams.get('procedencia').split(',')
      filterProcedencia = `&procedencia__or=${procedenciaArray
        .map((item) => `procedencia=${item}`)
        .join('%26')}`
    }

    if (searchParams.get('search') && searchParams.get('search') !== '') {
      search = `&__or=${['ens_title', 'title']
        .map((item) => `${item}=${searchParams.get('search')}`)
        .join('%26')}`
    }

    if (searchParams.get('sort-by') && searchParams.get('sort-direction')) {
      order = `&_sort_${searchParams.get('sort-direction')}=${searchParams.get('sort-by')}`
    }

    return `${filterPerfil}${filterInterestArtistic}${filterProcedencia}${search}${order}`
  }

  const { data: { items_total: itemsTotal } = 0 } = useGuillotina({
    path: `${edition}/@search?type_name__in=AcreditacioProfessional,AcreditacioPremsa&b_start=0&b_size=1`,
  })

  const getPaginationFilter = () => {
    let paginationFilter = `&b_size=${PROFESSIONALS_ITEMS_PER_PAGE}`
    if (searchParams.get('page')) {
      paginationFilter = `&b_start=${
        (parseInt(searchParams.get('page')) - 1) * PROFESSIONALS_ITEMS_PER_PAGE
      }${paginationFilter}`
    } else paginationFilter = `&b_start=0${paginationFilter}`
    return paginationFilter
  }

  const currentPage = parseInt(searchParams.get('page')) || 1
  const favouritesFilter = searchParams.get('favourites')

  // Use server or client pagination depending on favorites filter state
  // Only one hook will execute at a time based on the enabled flag
  const serverPaginationData = useProfessionalsWithServerPagination({
    edition,
    filtersSearch: getFiltersSearch(),
    paginationFilter: getPaginationFilter(),
    enabled: !isFavouritesFilterActive,
  })

  const clientPaginationData = useProfessionalsWithClientPagination({
    edition,
    filtersSearch: getFiltersSearch(),
    favouriteIds: favouiteIds,
    currentPage,
    isShowingFavourites: favouritesFilter === 'yes',
    enabled: isFavouritesFilterActive,
  })

  // Select the correct data source
  const { isLoading, professionals, showingItems } = isFavouritesFilterActive
    ? clientPaginationData
    : serverPaginationData

  const { data: { items: vocabularyAmbits } = [] } = useGuillotina({
    path: `${edition}/@vocabularies/interes_artistic`,
    useStatic: true,
  })

  const filtersDefiniton = useMemo(() => {
    const filters = [
      {
        key: 'perfil',
        label: t('perfil_professional'),
        sorted: true,
        data: [
          { value: 'all', label: t('Veure’ls tots'), default: true },
          { value: 'artista', label: t('artista') },
          { value: 'representant', label: t('representant') },
          { value: 'professional', label: t('professional') },
          { value: 'premsa', label: t('premsa') },
        ],
      },
    ]
    if (vocabularyAmbits) {
      filters.push({
        key: 'interes_artistic',
        label: t('Àmbit'),
        sorted: true,
        data: [
          { value: 'all', label: t('show_all'), default: true },
          ...vocabularyAmbits.map((ambit) => ({
            value: ambit.token,
            label: t(ambit.title),
          })),
        ],
      })
    }

    if (vocabularyAmbits) {
      filters.push({
        key: 'procedencia',
        label: t('Procedència'),
        sorted: true,
        data: [
          { value: 'all', label: t('Veure-les totes'), default: true },
          ...PROCEDENCIES_PROFESSIONAL.map((procedence) => ({
            value: procedence,
            label: t(procedence),
          })),
        ],
      })
    }
    filters.push({
      key: 'favourites',
      label: t('favourites'),
      sorted: true,
      unique: true,
      data: [
        { value: 'all', label: t('Veure’ls tots'), default: true },
        { value: 'yes', label: t('si') },
        { value: 'no', label: t('no') },
      ],
    })
    return filters
  }, [currentEdition, vocabularyAmbits, currentLanguage])

  return (
    <Box className={'professionals-list-page-container page-wrapper'}>
      <Box display={'flex'} alignItems={'end'} justifyContent={'space-between'} flexWrap={'wrap'}>
        <Box maxWidth={'500px'}>
          <h1>{t('Professionals')}</h1>
          <p>{t('professionals_page_detail', { edition: edition })}</p>
        </Box>
        <FilterSearch
          placeholder={t('professionals_search')}
          onChange={({ target }) => {
            searchParams.set('search', target.value)
            searchParams.set('page', 1)
            setSearchParams(searchParams)
          }}
          dataTest="inputSearchScheduleTest"
          value={searchParams.get('search') || ''}
          variant={'outlined'}
        />
      </Box>
      <Filter
        itemsTotal={itemsTotal}
        showingItems={showingItems || 0}
        filtersDefiniton={filtersDefiniton}
      />
      <ProfessionalTable
        key={`professionalTable_${
          isFavouritesFilterActive ? 'client' : 'server'
        }_${getFiltersSearch()}${!isFavouritesFilterActive ? getPaginationFilter() : currentPage}`}
        professionals={professionals}
        loading={isLoading}
        onClick={(professional, ens_type_name) => {
          setProfessionalEntityStack([...professionalEntityStack, professional])
        }}
        gridView={searchParams.get(VIEW_TYPE_KEY) === VIEW_TYPE_GRID}
      />
      {!isLoading && (
        <Pagination itemsTotal={showingItems} maxItems={PROFESSIONALS_ITEMS_PER_PAGE} />
      )}
      <ProfessionalEntityDialog
        uuid={professionalEntityStack.length > 0 ? professionalEntityStack.at(-1) : null}
        onClose={() => setProfessionalEntityStack([])}
        onClick={(uuid) => setProfessionalEntityStack([...professionalEntityStack, uuid])}
        onBack={() => {
          const newProfessionalEntityStack = [...professionalEntityStack]
          newProfessionalEntityStack.splice(-1)
          setProfessionalEntityStack(newProfessionalEntityStack)
        }}
      />
    </Box>
  )
}

export default ProfessionalsListPage
